import React, { Component, PropsWithChildren, ReactElement } from 'react';
import Scrollbar from 'react-custom-scrollbars';
import { createPortal } from 'react-dom';
import { code, codes } from '../utils/keyCode';
import { DropdownButtonContext } from './DropdownButton';
import { FocusHelper } from './FocusHelper';
import { IDropdownListProps, IMenuItemProps } from './types';

export class DropdownList extends Component<PropsWithChildren<IDropdownListProps>, { height: number; y: number }> {
  public state = {
    height: 100,
    y: 0,
  };
  private el: HTMLUListElement | null;

  public componentDidMount() {
    window.document.addEventListener('keydown', this.handleKeyboardEvent);
  }

  public componentWillUnmount() {
    window.document.removeEventListener('keydown', this.handleKeyboardEvent);
  }

  public render() {
    const { className, align, elContainer, height, labeledById, onSelect, width } = this.props;
    let meta: any;

    // determine x,y for the dropdown
    if (elContainer) {
      meta = elContainer.getBoundingClientRect();
      const xAlignLeft = meta.left + window.pageXOffset;
      const xAlignRight = xAlignLeft - width + meta.width;
      meta.x = align === 'left' ? xAlignLeft : xAlignRight;
      meta.y = meta.top + window.pageYOffset + meta.height;
    } else {
      meta = { x: 0, y: 0, height: 0 };
    }
    const list = (
      <DropdownButtonContext.Consumer>
        {(context) => {
          return (
            <div
              className={`hlc-dropdownButtonList ignore-external-click ${className}`}
              style={{
                left: meta.x,
                top: this.state.y === 0 ? meta.y : this.state.y,
                width,
              }}
            >
              {this.props.renderInfo && <div className="hlc-dropdownButton-info">{this.props.renderInfo()}</div>}

              <Scrollbar style={{ height: Math.min(height, this.state.height) + 5 }} hideTracksWhenNotNeeded={true}>
                <ul
                  className={`dropdown-menu`} // this is a bootstrap class
                  aria-labelledby={labeledById}
                  ref={(el) => {
                    this.el = el;
                    if (el) {
                      el.focus(); // so we can tab through the menuitems

                      // save height of this node to state
                      this.setState((prevState) => {
                        if (prevState.height !== el.clientHeight) {
                          return { height: el.clientHeight };
                        } else {
                          return null;
                        }
                      });

                      this.maybeAdjustVerticalPosition(el, meta.height, meta.y);
                    }
                  }}
                  tabIndex={0}
                >
                  {React.Children.toArray(this.props.children)
                    .filter((child: any) => !!child) // filter out falsey children (e.g. null, false, '')
                    .map((menuitem: ReactElement<IMenuItemProps>) => {
                      const { disabled, onClick, onSelect: menuitemOnSelect, releaseFocusAfterSelect } = menuitem.props;
                      return React.cloneElement(menuitem, {
                        onClick: disabled
                          ? () => {
                              /*
                               * The idea with this noop fn is to prevent closing the DropdownList when a disabled
                               * MenuItem is clicked.  However it doesn't work because the ExternalClickWrapper
                               * around DropdownButton is causing the DropdownList to close because DropdownList is portaled.
                               */
                            }
                          : (e: React.MouseEvent<HTMLElement>) => {
                              const keepFocus = releaseFocusAfterSelect === true ? false : true;
                              context.releaseFocus(keepFocus);
                              onClick && onClick(e);
                              this.handleClose();
                            },
                        onSelect: (key: any, e: React.MouseEvent<HTMLElement>) => {
                          menuitemOnSelect && menuitemOnSelect(key, e);
                          onSelect && onSelect(key);
                        },
                      });
                    })}
                </ul>
              </Scrollbar>
            </div>
          );
        }}
      </DropdownButtonContext.Consumer>
    );

    return createPortal(list, document.body);
  }

  // if dropdownList is near the viewport bottom then re-position above the button
  private maybeAdjustVerticalPosition(el: HTMLUListElement, buttonHeight: number, yDefault: number) {
    const meta = el.getBoundingClientRect();
    const elementTop = meta.top + window.pageYOffset;
    const elementBottom = elementTop + el.clientHeight;
    const viewportTop = window.pageYOffset;
    const viewportBottom = viewportTop + window.innerHeight;
    let y = 0;

    if (elementBottom + 5 >= viewportBottom) {
      y = Math.round(yDefault - el.clientHeight - buttonHeight - 5);
    }

    // we only want to do this one time
    this.setState((prevState) => {
      if (prevState.y === 0 && y > 0) {
        return { y };
      } else {
        return null;
      }
    });
  }

  private handleClose = () => {
    this.props.onClose();
  };

  // listening for up/down arrow, tab
  private handleKeyboardEvent = (e: KeyboardEvent) => {
    const F = new FocusHelper(this.el);

    if (F.focusable.length === 0) {
      return;
    }

    const { keyCode: key } = e;
    const keysOfInterest = 'up down tab'.split(' ');
    const isKeyOfInterest: boolean = key != null && codes(keysOfInterest).indexOf(key) !== -1;

    if (!isKeyOfInterest) {
      return;
    }

    /***********   handle TAB   *************/
    const isTab = key === code('tab');
    const isShiftTab = isTab && e.shiftKey;
    const isNormalTab = isTab && !e.shiftKey;
    const activeElement = document.activeElement;

    // if we are about to tab away then close the dropdown
    if ((isShiftTab && activeElement === F.first) || (isNormalTab && activeElement === F.last)) {
      this.props.onClose();
      e.preventDefault();
    }

    /***********   handle UP/DOWN   *************/
    if (key === code('up') || key === code('down')) {
      const method = key === code('up') ? 'previous' : 'next';
      F[method].focus();
      e.preventDefault();
    }
  };
}
